import {
    ChangeDetectionStrategy,
    Component,
    inject,
    ViewChild,
} from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { AgGridAngular } from 'ag-grid-angular';
import {
    ColDef,
    GridApi,
    GridOptions,
    GridReadyEvent,
    ICellRendererParams,
} from 'ag-grid-community';
import { Observable, takeUntil, tap } from 'rxjs';
import { RouteConstants } from '../../../../core/constants/routes';
import {
    nameOf,
    replacePipeWithNewline,
} from '../../../../core/functions/string-helpers';
import {
    FormGroupType,
    NestedFormGroupType,
} from '../../../../core/models/form-group-type';
import { GridHelper } from '../../../../core/models/grid/grid-helper';
import { SingleActionCellRendererParams } from '../../../../core/models/grid/single-action-cell-renderer-params';
import { FormEntryService } from '../../../../core/services/form-entry/form-entry.service';
import { FrequencyFormattingService } from '../../../../core/services/frequency-formatting/frequency-formatting.service';
import { ReturnRouteService } from '../../../../core/services/navigation/return-route.service';
import { ValidationResult } from '../../../../core/services/validation/models/validation-result';
import { CheckmarkDialogComponent } from '../../../../shared/components/checkmark-dialog/checkmark-dialog.component';
import { SingleActionCellRendererBaseComponent } from '../../../../shared/components/grid/cell-renderers/single-action-cell-renderer-base/single-action-cell-renderer-base.component';
import { GridToolbarComponent } from '../../../../shared/components/grid/grid-toolbar/grid-toolbar.component';
import { GridToolbarOptions } from '../../../../shared/components/grid/grid-toolbar/models/grid-toolbar-options';
import { ModalComponent } from '../../../../shared/components/modal/modal.component';
import { ApplicationFilingStatusEnum } from '../../shared/constants/application-filing-status';
import { SubmissionToEwaCompleteDialogMessage } from '../../shared/constants/submission-dialog-message';
import { ApplicationFormControlManagerService } from '../../shared/services/application-form-control-manager/application-form-control-manager.service';
import { ApplicationStateService } from '../../shared/services/application-state/application-state.service';
import { ApplicationSubmitToFccService } from '../../shared/services/application-submit-to-fcc/application-submit-to-fcc.service';
import { StepNavigationService } from '../../shared/services/step-navigation/step-navigation.service';
import { ApplicationCoordinationBaseComponent } from '../shared/components/application-coordination-base/application-coordination-base.component';
import { CoordinationService } from '../shared/services/coordination/coordination.service';
import { CoordinateApplicationRequest } from '../shared/services/coordination/models/coordinate-application-request';
import { CoordinateApplicationResponse } from '../shared/services/coordination/models/coordinate-application-response';
import { CoordinationResultScheduleDAndHInfoComponent } from './components/coordination-result-schedule-d-and-h-info/coordination-result-schedule-d-and-h-info.component';
import { CoordinationResultScheduleDAndH } from './components/coordination-result-schedule-d-and-h-info/models/coordination-result-schedule-d-and-h';
import { CoordinationResultsFrequencyComponent } from './components/coordination-results-frequency/coordination-results-frequency.component';
import { CoordinationResultStudyDetailModalData } from './components/coordination-results-frequency/models/coordination-result-study-detail-modal-data';
import { CoordinationResultsTreeComponent } from './components/coordination-results-tree/coordination-results-tree.component';
import { CoordinationResultTreeNode } from './components/coordination-results-tree/models/coordination-result-tree-node';
import { CoordinationStudyCheckBoxCellRendererComponent } from './components/coordination-study-check-box-cell-renderer/coordination-study-check-box-cell-renderer/coordination-study-check-box-cell-renderer.component';
import { OverwriteCoordinationResultsDialogMessage } from './constants/overwrite-coordination-results-dialog-message';
import { SubmitForCoordinationDialogMessage } from './constants/submit-for-coordination-dialog-message';
import { SubmittedToEwaForFinalCertificationDialogMessage } from './constants/submitted-to-ewa-for-final-certification-dialog-message';
import { CoordinationResultStudyDetailRequest } from './models/coordination-result-study-detail-request';
import { CoordinationResultStudyDetailResponse } from './models/coordination-result-study-detail-response';
import { CoordinationResultStudyFormModel } from './models/coordination-result-study-form-model';
import { CoordinationResultsFormModel } from './models/coordination-results-form-model';
import { CoordinationResultsRequest } from './models/coordination-results-request';
import { CoordinationResultsResponse } from './models/coordination-results-response';
import { CoordinationSubmissionResponse } from './models/coordination-submission-response';
import { CoordinationResultStudyFormService } from './services/coordination-result-study-form/coordination-result-study-form.service';
import { CoordinationResultsFormService } from './services/coordination-results-form/coordination-results-form.service';
import { CoordinationResultsTreeService } from './services/coordination-results-tree/coordination-results-tree.service';

@Component({
    selector: 'app-coordination-results',
    imports: [
        CoordinationResultsTreeComponent,
        AgGridAngular,
        MatChipsModule,
        GridToolbarComponent,
        CoordinationResultScheduleDAndHInfoComponent,
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './coordination-results.component.html',
})
export class CoordinationResultsComponent extends ApplicationCoordinationBaseComponent<
    CoordinationResultsRequest,
    CoordinationResultsResponse,
    CoordinationResultsFormModel
> {
    treeDataSource: CoordinationResultTreeNode[] = [];
    treeSelectedNode: CoordinationResultTreeNode | null = null;
    treeSelectedNodeScheduleDAndH: CoordinationResultScheduleDAndH | null =
        null;
    treeSelectedNodeStudies: CoordinationResultStudyFormModel[] | null = [];

    response: CoordinationResultsResponse | null = null;

    gridApi: GridApi | undefined;

    @ViewChild(CoordinationResultScheduleDAndHInfoComponent)
    scheduleDAndHInfoComponent!: CoordinationResultScheduleDAndHInfoComponent;

    get studiesControl(): FormArray<
        FormGroup<FormGroupType<CoordinationResultStudyFormModel>>
    > {
        return this.form.controls.studies as FormArray<
            FormGroup<FormGroupType<CoordinationResultStudyFormModel>>
        >;
    }

    gridToolbarOptions: GridToolbarOptions = {
        refreshDataForExport: () => {},
        quickFilter: false,
        clearFilters: true,
        exportCsv: true,
        exportCsvOptions: {
            columnKeys: [
                nameOf<CoordinationResultStudyFormModel>('autoCoordRank'),
                nameOf<CoordinationResultStudyFormModel>('testFreq'),
                nameOf<CoordinationResultStudyFormModel>('autoCoordStatusCode'),
                nameOf<CoordinationResultStudyFormModel>('totalPositiveOvpP'),
                nameOf<CoordinationResultStudyFormModel>('comments'),
            ],
            fileName: () => {
                this.checkIfApplicationStateNull();

                return GridHelper.getExportFileName(
                    'Results_Location',
                    this.applicationState!.salmonLogId,
                );
            },
            prependContentFn: () => {
                return this.scheduleDAndHInfoComponent.getContentForExport();
            },
        },
    };

    columnDefs: ColDef[] = [
        {
            ...GridHelper.selectRowColDef,
            field: nameOf<CoordinationResultStudyFormModel>('isSelected'),
            cellRenderer: CoordinationStudyCheckBoxCellRendererComponent,
            cellRendererParams: {
                getFormGroupForStudy: this.getFormGroupForStudy.bind(this),
            },
        },
        {
            headerName: 'Rank',
            field: nameOf<CoordinationResultStudyFormModel>('autoCoordRank'),
            maxWidth: 70,
            filter: false,
        },
        {
            headerName: 'Frequency',
            field: nameOf<CoordinationResultStudyFormModel>('testFreq'),
            maxWidth: 200,
            cellDataType: 'number',
            sort: 'asc',
            sortIndex: 2,
            valueFormatter: (params) => {
                return params.value != null
                    ? this.frequencyFormattingService.getFormattedFrequency(
                          params.value,
                      )
                    : '';
            },
            filterParams: {
                ...GridHelper.defaultNumericFilterParams,
                filterPlaceholder: 'Frequency',
            },
        },
        {
            headerName: 'Pass/Fail',
            field: nameOf<CoordinationResultStudyFormModel>(
                'autoCoordStatusCode',
            ),
            sort: 'desc',
            sortIndex: 0,
            filter: false,
            wrapHeaderText: true,
            maxWidth: 150,
            headerStyle: {
                textAlign: 'center',
            },
        },
        {
            headerName: 'Cumulative Overlap %',
            field: nameOf<CoordinationResultStudyFormModel>(
                'totalPositiveOvpP',
            ),
            sort: 'asc',
            sortIndex: 1,
            maxWidth: 200,
            cellDataType: 'number',
            filterParams: {
                ...GridHelper.defaultNumericFilterParams,
                filterPlaceholder: 'Overlap %',
            },
            headerStyle: {
                textAlign: 'center',
            },
            wrapHeaderText: true,
            autoHeaderHeight: true,
        },
        {
            headerName: 'Comments',
            field: nameOf<CoordinationResultStudyFormModel>('comments'),
            sortable: false,
            cellStyle: {
                textAlign: 'left',
                justifyContent: 'flex-start',
                display: 'flex',
                alignItems: 'center', // keeps vertically centered, when single-line
                lineHeight: 1.6, // not double-spaced, when multi-line
                whiteSpace: 'pre-line', // preserves \n characters
            },
            resizable: false,
            minWidth: 230,
            cellRenderer: (params: ICellRendererParams) => {
                return replacePipeWithNewline(params.data.comments);
            },
        },
        {
            headerName: 'Details',
            maxWidth: 96,
            filter: false,
            sortable: false,
            field: nameOf<CoordinationResultStudyFormModel>('id'),
            cellRenderer: SingleActionCellRendererBaseComponent,
            cellRendererParams: {
                label: 'View',
                action: (params: SingleActionCellRendererParams) =>
                    this.openStudyDetailsModal(params.value),
            } as SingleActionCellRendererParams,
            ...GridHelper.disableCellFocusBorderColDef,
        },
    ];

    private readonly coordinationService = inject(CoordinationService);
    private readonly coordinationResultsTreeService = inject(
        CoordinationResultsTreeService,
    );
    private readonly formService = inject(CoordinationResultsFormService);
    private readonly studyFormService = inject(
        CoordinationResultStudyFormService,
    );
    private readonly dialog = inject(MatDialog);
    private readonly applicationSubmitToFccService = inject(
        ApplicationSubmitToFccService,
    );
    private readonly frequencyFormattingService = inject(
        FrequencyFormattingService,
    );

    gridOptions: GridOptions = {
        ...GridHelper.defaultGridOptions,
        defaultColDef: {
            ...GridHelper.defaultGridOptions.defaultColDef,
            sortable: true,
        },
        suppressMultiSort: false,
        columnDefs: this.columnDefs,
    };

    override form: FormGroup<
        NestedFormGroupType<CoordinationResultsFormModel>
    > = this.formService.getFormDefinition();

    constructor() {
        super(
            inject(ActivatedRoute),
            inject(StepNavigationService),
            inject(Router),
            inject(FormEntryService),
            inject(ApplicationStateService),
            inject(ApplicationFormControlManagerService),
            inject(ReturnRouteService),
        );
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    initializeGridContext(): any {
        return {
            formArray: this.form.controls.studies as FormArray,
        };
    }

    onGridReady(params: GridReadyEvent) {
        this.gridApi = params.api;
        this.updateGrid();
    }

    updateGrid(): void {
        this.gridApi?.setGridOption('rowData', this.treeSelectedNodeStudies);
    }

    onTreeLeafSelected(selectedNode: CoordinationResultTreeNode): void {
        if (this.treeSelectedNode !== selectedNode) {
            this.treeSelectedNode = selectedNode;

            this.treeSelectedNodeScheduleDAndH =
                this.coordinationResultsTreeService.findMatchingScheduleDAndH(
                    this.response,
                    selectedNode,
                );

            this.treeSelectedNodeStudies =
                this.treeSelectedNodeScheduleDAndH?.frequency.studies ?? null;

            this.updateGrid();
        }
    }

    private getTreeData(): CoordinationResultTreeNode[] {
        return this.coordinationResultsTreeService.transformCoordinationResultResponseToTreeData(
            this.response,
        );
    }

    private openStudyDetailsModal(studyId: string): void {
        this.checkIfApplicationIdNull();

        const request: CoordinationResultStudyDetailRequest = {
            applicationId: this.applicationId!,
            studyId: studyId,
        };

        this.coordinationService
            .getCoordinationResultStudyDetails(request)
            .pipe(
                takeUntil(this.destroy$),
                tap((response: CoordinationResultStudyDetailResponse) => {
                    this.checkIfApplicationStateNull();

                    const modalData: CoordinationResultStudyDetailModalData = {
                        applicationId: response.applicationId.toString(),
                        salmonLogId: this.applicationState!.salmonLogId,
                        details: response.details,
                    };

                    this.dialog.open(CoordinationResultsFrequencyComponent, {
                        data: modalData,
                        minWidth: '93vw',
                    });
                }),
            )
            .subscribe();
    }

    private getFormGroupForStudy(
        studyId: number,
    ): FormGroup<FormGroupType<CoordinationResultStudyFormModel>> | undefined {
        return this.studiesControl.controls.find(
            (ctrl) => ctrl.value.id === studyId,
        );
    }

    private checkIfApplicationIdNull(): void {
        if (!this.applicationId) {
            throw new Error('Application ID cannot be null');
        }
    }

    private checkIfApplicationStateNull(): void {
        if (!this.applicationState) {
            throw new Error('Application state cannot be null');
        }
    }

    private navigateToDashboard(): void {
        this.router.navigate([RouteConstants.Dashboard.getFullRoute('INDEX')]);
    }

    /**
     * Utility to open dialogs only if none are open
     */
    private openDialogIfNoneOpen(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        component: any,
        config: MatDialogConfig,
    ): void {
        if (this.dialog.openDialogs.length > 0) {
            return;
        }

        this.dialog.open(component, config);
    }

    private openSubmitToEwaCompleteDialog(): void {
        this.openDialogIfNoneOpen(CheckmarkDialogComponent, {
            disableClose: true,
            data: {
                title: SubmissionToEwaCompleteDialogMessage.modalTitle,
                content: SubmissionToEwaCompleteDialogMessage.modalContent,
                primaryAction: {
                    title: SubmissionToEwaCompleteDialogMessage.primaryActionTitle,
                    action: this.navigateToDashboard.bind(this),
                },
            },
        });
    }

    private openSubmittedToEwaForFinalCertificationDialog(): void {
        this.openDialogIfNoneOpen(CheckmarkDialogComponent, {
            disableClose: true,
            data: {
                title: SubmittedToEwaForFinalCertificationDialogMessage.modalTitle,
                content:
                    SubmittedToEwaForFinalCertificationDialogMessage.modalContent,
                primaryAction: {
                    title: SubmittedToEwaForFinalCertificationDialogMessage.primaryActionTitle,
                    action: this.navigateToDashboard.bind(this),
                },
            },
        });
    }

    private openOverwriteCoordinationResultsDialog(): void {
        this.openDialogIfNoneOpen(ModalComponent, {
            disableClose: true,
            data: {
                title: OverwriteCoordinationResultsDialogMessage.modalTitle,
                content: OverwriteCoordinationResultsDialogMessage.modalContent,
                primaryAction: {
                    title: OverwriteCoordinationResultsDialogMessage.primaryActionTitle,
                    action: this.submitForCoordination.bind(this),
                },
                showCancel: true,
            },
        });
    }

    private openSubmitForCoordinationDialog(): void {
        this.openDialogIfNoneOpen(CheckmarkDialogComponent, {
            disableClose: true,
            data: {
                title: SubmitForCoordinationDialogMessage.modalTitle,
                content: SubmitForCoordinationDialogMessage.modalContent,
                primaryAction: {
                    title: SubmitForCoordinationDialogMessage.primaryActionTitle,
                    action: this.navigateToDashboard.bind(this),
                },
            },
        });
    }

    private submitForCoordination(): void {
        this.checkIfApplicationIdNull();

        const coordinateApplicationRequest: CoordinateApplicationRequest = {
            applicationId: this.applicationId!,
        };

        this.coordinationService
            .submitForCoordination(coordinateApplicationRequest)
            .pipe(
                tap((_: CoordinateApplicationResponse) => {
                    this.openSubmitForCoordinationDialog();
                }),
            )
            .subscribe();
    }

    private handleSubmitToEwaResponse(
        response: CoordinationSubmissionResponse,
    ): void {
        if (
            response.filingStatus ===
            ApplicationFilingStatusEnum.submittedToEwaForCertification
        ) {
            this.openSubmittedToEwaForFinalCertificationDialog();
        } else {
            this.openSubmitToEwaCompleteDialog();
        }
    }

    override postFormBindingActions(): void {
        this.treeDataSource = this.getTreeData();
    }

    protected override submitForCoordinationAction(): void {
        this.checkIfApplicationIdNull();

        this.coordinationService
            .getCoordinationStatus(this.applicationId!)
            .pipe(
                tap((status) => {
                    if (status.hasCoordinationResults) {
                        this.openOverwriteCoordinationResultsDialog();
                    } else {
                        this.submitForCoordination();
                    }
                }),
            )
            .subscribe();
    }

    protected override handoverToEwaAction(): void {
        this.checkIfApplicationIdNull();

        this.coordinationService
            .submitToEwa(this.applicationId!)
            .pipe(
                takeUntil(this.destroy$),
                tap((response: CoordinationSubmissionResponse) =>
                    this.handleSubmitToEwaResponse(response),
                ),
            )
            .subscribe();
    }

    protected override submitToFccAction(): void {
        this.checkIfApplicationIdNull();

        const userId =
            this.applicationState?.applicationUserInformation?.userId;

        this.applicationSubmitToFccService
            .submitToFcc(this.applicationId!, userId ?? null)
            .subscribe();
    }

    protected override bindForm(response: CoordinationResultsResponse): void {
        this.response = response;

        const studies = response.locations
            .flatMap((l) => l.antennas)
            .flatMap((a) => a.frequencies)
            .flatMap((f) => f.studies);

        this.studiesControl.clear();

        studies.forEach(() => {
            this.studiesControl.push(this.studyFormService.getFormDefinition());
        });

        this.studiesControl.patchValue(studies);
    }

    protected override getFormDataFromApi(
        applicationId: string,
    ): Observable<CoordinationResultsResponse> {
        return this.coordinationService.getCoordinationResults(applicationId);
    }

    protected override saveFormDataToApi(
        request: CoordinationResultsRequest,
    ): Observable<ValidationResult> {
        return this.coordinationService.saveCoordinationResults(request);
    }

    protected override generateRequestObject(): CoordinationResultsRequest {
        const selectedResults = this.studiesControl.controls
            .filter((control) => control.dirty)
            .map((control) => ({
                id: control.value.id,
                isSelected: control.value.isSelected,
            }));

        return {
            ...(selectedResults.length > 0 ? { selectedResults } : {}),
            applicationId: this.applicationId!,
        };
    }
}
